import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  LICENSE_PUBLIC_KEYS,
  clockAtContact,
  licenseSchedule,
  tickClock,
  verifyLicenseToken,
  type DeviceClock,
  type LicenseTokenPayload,
} from '@pos/core';
import { getMeta, setMeta, type CachedLicense } from '@pos/local-db';
import { db } from '../lib/db.ts';
import { FunctionError, callFunction, supabase } from '../lib/supabase.ts';
import { canWriteState, deviceLicenseState, type DeviceLicenseState } from './license-state.ts';

// Licencia del negocio en este dispositivo: se consulta al servidor al abrir, cada 30 minutos y
// al volver la conexión; se guarda para funcionar sin conexión. En modo consulta (solo lectura o
// suspendida) se frena toda escritura local: ninguna operación entra a la cola de envío.
//
// Etapa 8B: el servidor entrega una credencial firmada (Edge Function `license`). Se verifica con
// la clave pública incluida en la app, al recibirla y cada vez que se abre (si alguien la altera
// en el dispositivo, deja de valer). Cada cambio se guarda con el jti de la credencial vigente.

let cached: CachedLicense | undefined;
/** Contenido verificado de la credencial guardada (undefined = sin verificar todavía). */
let verified: LicenseTokenPayload | null | undefined;
let clock: DeviceClock | undefined;
let businessId: string | null = null;

export class LicenseBlockedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LicenseBlockedError';
  }
}

function current(): DeviceLicenseState {
  return deviceLicenseState(cached && cached.business_id === businessId ? cached : undefined, { verified, clock });
}

export function blockedMessage(state: DeviceLicenseState): string {
  if (state.kind === 'stale') return 'Hace muchos días que este dispositivo no se conecta. Conectate a internet para seguir cargando datos.';
  if (state.kind === 'clock') return 'La fecha y hora de este dispositivo están atrasadas. Corregilas (o conectate a internet) para seguir cargando datos.';
  if (state.kind === 'ok' && state.status === 'suspendida') return 'La suscripción está suspendida: no se pueden cargar datos. Comunicate para regularizarla.';
  return 'La suscripción está en modo consulta: podés ver todo, pero no cargar ventas ni cambios. Regularizá el pago para seguir.';
}

/**
 * Se instala una vez al arrancar: frena las escrituras locales si la licencia no lo permite, y
 * a las permitidas les pone la credencial con la que se hicieron.
 */
export function installLicenseGuard(): void {
  db.outbox.hook('creating', (_key, op) => {
    const state = current();
    if (!canWriteState(state)) throw new LicenseBlockedError(blockedMessage(state));
    if (state.kind === 'ok' && state.jti) op.lic_jti = state.jti;
  });
}

/** Verifica una credencial: firma válida, de este negocio y de este dispositivo. */
async function verify(token: string, business: string): Promise<LicenseTokenPayload | null> {
  const payload = await verifyLicenseToken(token, LICENSE_PUBLIC_KEYS);
  const device = await getMeta(db, 'device');
  return payload && payload.sub === business && payload.aud === device?.id ? payload : null;
}

/** Negocio activo en el dispositivo (carga el último estado guardado y verifica su credencial). */
let loading: Promise<void> = Promise.resolve();

export function setLicenseBusiness(id: string | null): Promise<void> {
  businessId = id;
  verified = undefined;
  loading = (async () => {
    [cached, clock] = id ? await Promise.all([getMeta(db, 'license'), getMeta(db, 'clock')]) : [undefined, undefined];
    if (id && cached?.token) verified = await verify(cached.token, id);
  })();
  return loading;
}

interface LicenseResponse {
  token: string;
  usage: CachedLicense['usage'];
}

/** Pide la credencial al servidor (si hay sesión online), la verifica y la guarda. */
export async function refreshLicense(deviceId?: string | null): Promise<void> {
  // Primero lo guardado (si no, lo que llega del servidor podría pisarse con lo viejo).
  await loading;
  const business = businessId;
  if (!business || !navigator.onLine) return;
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) return;

  if (deviceId) {
    try {
      const res = await callFunction<LicenseResponse>('license', { business_id: business, device_id: deviceId });
      const payload = await verify(res.token, business);
      if (payload) {
        const schedule = licenseSchedule(payload.lic, new Date(payload.srv));
        cached = {
          business_id: business,
          status: schedule.status,
          plan: payload.plan,
          ...payload.lic,
          server_time: new Date(payload.srv).toISOString(),
          usage: res.usage,
          fetched_at: new Date().toISOString(),
          token: res.token,
        };
        verified = payload;
        clock = clockAtContact(payload.srv, Date.now());
        await Promise.all([setMeta(db, 'license', cached), setMeta(db, 'clock', clock)]);
        return;
      }
      console.warn('[licencia] la credencial recibida no se pudo verificar (¿falta publicar la clave pública?)');
    } catch (error) {
      // Servidor sin la clave de firma (o función no publicada): se sigue como en la 8A.
      if (!(error instanceof FunctionError)) throw error;
      console.warn('[licencia] sin credencial firmada:', error.message);
    }
  }

  const { data, error } = await supabase.rpc('license_info', { p_business_id: business });
  if (error || !data) return;
  const info = data as Omit<CachedLicense, 'business_id' | 'fetched_at'>;
  cached = { ...info, business_id: business, fetched_at: new Date().toISOString(), token: null };
  verified = undefined;
  await setMeta(db, 'license', cached);
}

/** Estado de la licencia en vivo (se recalcula cada minuto: avanza por fechas sin conexión). */
export function useLicenseState(): DeviceLicenseState {
  const meta = useLiveQuery(async () => ({ license: await getMeta(db, 'license'), clock: await getMeta(db, 'clock') }), []);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  void tick;
  const license = meta?.license && meta.license.business_id === businessId ? meta.license : undefined;
  // La credencial verificada es la de memoria: lo guardado se vuelve a verificar al abrir.
  const sameToken = license?.token && cached?.token === license.token;
  return deviceLicenseState(license, { verified: sameToken ? verified : undefined, clock: meta?.clock });
}

export function useCanWrite(): boolean {
  return canWriteState(useLicenseState());
}

/**
 * Mientras la app está abierta: refresca la licencia (al abrir, cada 30 minutos y al volver la
 * conexión) y cada minuto suma el tiempo de uso al reloj protegido.
 */
export function startLicenseRefresh(deviceId: string | null): () => void {
  const run = () => void refreshLicense(deviceId).catch(() => undefined);
  let lastPerf = performance.now();
  const tick = () => {
    const nowPerf = performance.now();
    if (clock) {
      clock = tickClock(clock, Date.now(), nowPerf - lastPerf);
      void setMeta(db, 'clock', clock).catch(() => undefined);
    }
    lastPerf = nowPerf;
  };
  window.addEventListener('online', run);
  const interval = setInterval(run, 30 * 60_000);
  const clockInterval = setInterval(tick, 60_000);
  // Al abrir: en el próximo turno, cuando la app ya indicó de qué negocio es (setLicenseBusiness
  // corre en un efecto del componente padre, después de este).
  const first = setTimeout(run, 0);
  return () => {
    window.removeEventListener('online', run);
    clearTimeout(first);
    clearInterval(interval);
    clearInterval(clockInterval);
  };
}
